
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function HomeAutomationSystemsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Home Automation Systems',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Complete home automation system installation in Fresno, CA. Integrate lighting, climate, security, and entertainment with centralized smart home control.',
    url: `${siteUrl}/services/smart-home-energy/home-automation-systems`,
  };

  return (
    <>
      <SEOHead
        title="Home Automation Systems Fresno CA | Whole Home Smart Control"
        description="Complete home automation installation in Fresno, CA. Integrate lighting, climate, security, and entertainment. Voice control, automated scenes, and centralized management."
        canonical="/services/smart-home-energy/home-automation-systems"
        keywords="home automation Fresno, smart home systems, whole home automation, integrated smart home"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Home Automation Systems — Fresno, CA"
        heroSubtitle="Complete smart home integration for modern Fresno living"
        heroImage="https://public.readdy.ai/ai/img_res/bb65d239cac1842944975f4efacf501a.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Smart%20home%20control%20tablet%20displaying%20integrated%20automation%20dashboard%20with%20lighting%20climate%20security%20and%20entertainment%20controls%20in%20contemporary%20living%20room%20with%20connected%20devices%20visible%20no%20people%20sleek%20design&width=700&height=600&seq=homeauto-fresno-01&orientation=portrait"
        fresnoContent="Fresno homeowners are embracing whole-home automation that integrates lighting, climate control, security, and entertainment into one seamless system. Imagine arriving home to lights that turn on automatically, AC that's pre-cooled your home during Fresno's hot afternoon, and your favorite music playing—all triggered by your arrival. We design and install comprehensive automation systems tailored to Fresno's climate and lifestyle, serving Woodward Park, Fig Garden, Tower District, and all surrounding areas with professional installations that transform how you live."
        features={[
          { icon: 'ri-home-gear-line', title: 'Centralized Control Hub', description: 'Manage all systems from one interface' },
          { icon: 'ri-lightbulb-flash-line', title: 'Integrated Lighting Control', description: 'Automated scenes and schedules' },
          { icon: 'ri-temp-hot-line', title: 'Climate Management', description: 'Smart thermostats and zone control' },
          { icon: 'ri-shield-check-line', title: 'Security Integration', description: 'Cameras, locks, and sensors unified' },
          { icon: 'ri-voice-recognition-line', title: 'Voice Control', description: 'Alexa, Google, and Siri compatibility' },
          { icon: 'ri-smartphone-line', title: 'Remote Access', description: 'Control your home from anywhere' },
        ]}
        whatsIncluded={[
          'Complete home automation consultation and design',
          'Smart hub installation and network setup',
          'Lighting system integration and automation',
          'Climate control integration with smart thermostats',
          'Security system integration (cameras, locks, sensors)',
          'Entertainment system integration',
          'Voice assistant setup (Alexa, Google, Siri)',
          'Automated scene creation (Good Morning, Away, Night)',
          'Mobile app configuration and user training',
          'Ongoing support and system optimization',
        ]}
        faqs={[
          {
            question: 'What can I automate in my Fresno home?',
            answer:
              "You can automate virtually everything electrical in your Fresno home: lighting (indoor and outdoor), climate control (thermostats, ceiling fans), security (cameras, smart locks, sensors), entertainment (TVs, speakers), window treatments (motorized blinds), irrigation systems, and even appliances. We create automated scenes like \"Good Morning\" (lights on, thermostat adjusts, coffee maker starts) or \"Away Mode\" (lights simulate occupancy, AC reduces cooling, security arms)—perfect for Fresno's lifestyle and climate.",
          },
          {
            question: 'How much does whole-home automation cost in Fresno?',
            answer:
              'Basic home automation (smart lighting, thermostat, and security) for a typical Fresno home starts around $3,000-$5,000. Comprehensive systems integrating lighting, climate, security, entertainment, and motorized window treatments range from $10,000-$25,000 depending on home size and features. The investment adds significant value to your Fresno home and pays for itself through energy savings, especially during our expensive cooling season. We offer flexible packages to fit any budget.',
          },
          {
            question: 'Will home automation work with my existing devices in Fresno?',
            answer:
              "Most likely! We specialize in integrating existing smart devices into unified systems. If you already have smart lights, thermostats, or security cameras, we can often incorporate them into your automation system. We use platforms like Home Assistant, SmartThings, or Hubitat that support hundreds of devices. For devices that don't integrate, we recommend compatible replacements that work seamlessly with your Fresno home's automation system.",
          },
          {
            question: 'Is home automation reliable during Fresno power outages?',
            answer:
              'Home automation systems require power, but we can integrate battery backup for critical components like smart locks and security systems. Many smart hubs have battery backup built-in, maintaining your automation rules during brief outages. For Fresno homes concerned about summer power outages during heat waves, we recommend whole-home battery backup systems that keep your entire automation system running, including AC control and security monitoring.',
          },
          {
            question: 'Can I expand my home automation system later in Fresno?',
            answer:
              'Absolutely! We design systems with expansion in mind. Start with basic lighting and climate control, then add security, entertainment, or motorized window treatments later. All our installations use scalable platforms that easily accommodate new devices. Many Fresno homeowners start with one or two rooms and gradually automate their entire home. We provide ongoing support as you expand your system.',
          },
          {
            question: 'How long does home automation installation take in a Fresno home?',
            answer:
              'Basic automation (smart hub, 10-15 lights, thermostat, and security) takes 2-3 days. Comprehensive whole-home automation with integrated lighting, climate, security, entertainment, and motorized window treatments takes 5-7 days for a typical Fresno home. We work efficiently, test thoroughly, and train you on all features. Most homeowners can use their home normally during installation, and we clean up completely when finished.',
          },
        ]}
        ctaText="Design Your Smart Home System"
      />
    </>
  );
}
